<template>
  <div v-if="show" class="modal fade show" tabindex="-1" style="display: block; background: rgba(0, 0, 0, 0.5)">
    <div class="modal-dialog modal-dialog-centered max-width-65">
        <div class="modal-content border-radius-20 position-relative">
          <button type="button" class="close-modal-btn" @click="$emit('close')">
            <i class="ti ti-close"></i>
          </button>
          
          <div class="modal-body padd-modal">
            <h3 class="modal-title-new text-center">
              {{ currentIndex + 1 }} article{{ totalProducts > 1 ? 's' : '' }} sur {{ totalProducts }} à renseigner
            </h3>

            <div class="carousel-wrapper-new mb-5">
              <button class="btn-carousel-new prev" @click="prevProduct" :disabled="currentIndex === 0">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
              </button>

              <div class="product-info-card d-flex gap-4 align-items-center justify-content-center">
                <div class="product-image-frame">
                  <img :src="currentProduct.image" :alt="currentProduct.name" class="img-fluid product-img" />
                </div>
                <div class="product-details-new">
                  <span class="category-text">{{ currentProduct.category }}</span>
                  <h4 class="product-name-new">{{ currentProduct.name }}</h4>
                  <p class="product-ref-new">REF : {{ currentProduct.ref }}</p>
                  <p class="product-desc-new" v-html="currentProduct.description"></p>
                </div>
              </div>

              <button class="btn-carousel-new next" @click="nextProduct" :disabled="currentIndex === totalProducts - 1">
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </button>
            </div>

            <div class="pricing-container-new mb-4 text-start">
              <div class="section-divider">
                <span class="divider-text text-uppercase">Vérifier et confirmer les prix avant de le publier sur le site</span>
              </div>

              <div class="row g-3">
                <div class="col-md-6 px-3">
                  <div class="input-form">
                    <label class="label-control">Prix d'achat</label>
                    <input type="text" class="form-control" :value="formatPrice(currentProduct.prixAchat)" disabled />
                  </div>
                </div>
                <div class="col-md-6 px-3">
                  <div class="input-form">
                    <label class="label-control">Coef de marge</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      class="form-control" 
                      v-model.number="currentProduct.coef"
                      @input="onCoefInput"
                    />
                  </div>
                </div>
                <div class="col-md-6 px-3">
                  <div class="input-form">
                    <label class="label-control">Prix de vente HT</label>
                    <div class="input-group-pill">
                      <input 
                        type="number" 
                        step="0.01" 
                        class="form-control pill-left" 
                        v-model.number="currentProduct.prixVenteHT"
                        @input="onPriceHTInput"
                      />
                      <span class="input-group-addon-pill">€</span>
                    </div>
                  </div>
                </div>
                <div class="col-md-6 px-3">
                  <div class="input-form">
                    <label class="label-control">Prix vente TTC</label>
                    <input type="text" class="form-control" :value="formatPrice(currentProduct.prixTTC)" disabled />
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-footer-new d-flex justify-content-between align-items-center mt-4">
              <button class="btn-apply-recommended" @click="toggleRecommended">
                <div class="custom-checkbox-wrapper">
                  <input type="checkbox" :checked="currentProduct.appliquerPrixConsille" @change.stop />
                  <span class="custom-checkbox"></span>
                </div>
                Appliquer le prix de vente public conseillé
              </button>
              <button class="btn-validate-new" @click="validateProduct">
                Valider
              </button>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'NewProductsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    products: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'confirm'],
  setup(props, { emit }) {
    const currentIndex = ref(0);
    const totalProducts = computed(() => props.products.length);
    
    // We work on a local copy to enable local updates without affecting parent immediately
    const localProducts = ref([]);

    watch(() => props.products, (newVal) => {
      localProducts.value = JSON.parse(JSON.stringify(newVal));
    }, { immediate: true });

    const currentProduct = computed(() => {
      const p = localProducts.value[currentIndex.value];
      if (!p) return { prixAchat: 0, coef: 0, prixVenteHT: 0, prixTTC: 0, tva: 0.20, appliquerPrixConsille: true };
      
      // Initialize if not already done
      if (p.appliquerPrixConsille === undefined) {
        p.appliquerPrixConsille = true;
        p.prixVenteHT = p.recommendedPrice || p.prixAchat * 2.2;
        recalculateCoef(p);
      }
      return p;
    });

    const recalculateCoef = (p) => {
      if (p.prixAchat > 0 && p.prixVenteHT > 0) {
        p.coef = parseFloat((p.prixVenteHT / p.prixAchat).toFixed(2));
      } else {
        p.coef = 2.2;
      }
      updateTTC(p);
    };

    const formatPrice = (value) => {
      if (value === undefined || value === null) return '0,00 €';
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value) + ' €';
    };

    const onCoefInput = () => {
      const p = currentProduct.value;
      if (p.prixAchat && p.coef) {
        p.prixVenteHT = parseFloat((p.prixAchat * p.coef).toFixed(2));
        // If modified HT differs from recommended, uncheck
        if (p.prixVenteHT !== p.recommendedPrice) {
          p.appliquerPrixConsille = false;
        }
        updateTTC(p);
      }
    };

    const onPriceHTInput = () => {
      const p = currentProduct.value;
      if (p.prixAchat && p.prixVenteHT) {
        p.coef = parseFloat((p.prixVenteHT / p.prixAchat).toFixed(2));
        // If modified HT differs from recommended, uncheck
        if (p.prixVenteHT !== p.recommendedPrice) {
          p.appliquerPrixConsille = false;
        } else {
          p.appliquerPrixConsille = true;
        }
        updateTTC(p);
      }
    };

    const updateTTC = (p) => {
      const tvaRate = p.tva || 0.20;
      if (p.prixVenteHT !== undefined) {
        p.prixTTC = parseFloat((p.prixVenteHT * (1 + tvaRate)).toFixed(2));
      }
    };

    const toggleRecommended = () => {
      const p = currentProduct.value;
      p.appliquerPrixConsille = !p.appliquerPrixConsille;
      if (p.appliquerPrixConsille) {
        p.prixVenteHT = p.recommendedPrice;
        recalculateCoef(p);
      }
    };

    const prevProduct = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
      }
    };

    const nextProduct = () => {
      if (currentIndex.value < totalProducts.value - 1) {
        currentIndex.value++;
      }
    };

    const validateProduct = () => {
      const p = currentProduct.value;
      emit('confirm', {
        id: p.id,
        payload: {
          coef: parseFloat(p.coef.toFixed(2)),
          prixVenteHT: parseFloat(p.prixVenteHT.toFixed(2)),
          appliquerPrixConsille: p.appliquerPrixConsille
        }
      });
    };

    // Watch for products change to adjust index if current product was removed
    watch(() => props.products.length, (newLength, oldLength) => {
      if (newLength < oldLength) {
        if (currentIndex.value >= newLength && newLength > 0) {
          currentIndex.value = newLength - 1;
        } else if (newLength === 0) {
          emit('close');
        }
      }
    });

    return {
      currentIndex,
      totalProducts,
      currentProduct,
      formatPrice,
      onCoefInput,
      onPriceHTInput,
      toggleRecommended,
      prevProduct,
      nextProduct,
      validateProduct
    };
  }
};
</script>

<style scoped>
.max-width-65 {
  max-width: 65% !important;
}

.border-radius-20 {
  border-radius: 20px;
}

.padd-modal {
  padding: 40px 40px 40px 40px;
}

.modal-title-new {
  color: #2b3d8f;
  font-weight: 700;
  font-size: 26px;
  margin-bottom: 40px !important;
}

/* Close button */
.close-modal-btn {
  background: white;
  border: 1.5px solid #2b3d8f;
  color: #2b3d8f;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  position: absolute;
  top: 20px;
  right: 20px;
  transition: all 0.2s;
  padding: 0;
  z-index: 10;
}

.close-modal-btn:hover {
  background: #2b3d8f;
  color: white;
  transform: scale(1.1);
}

.text-primary {
  color: #2b3d8f !important;
}

/* Carousel */
.carousel-wrapper-new {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 40px;
}

.btn-carousel-new {
  border: 1px solid #1e347d !important;
  background: rgba(239, 241, 247, 0.95) !important;
  color: #1e347d;
  width: 45px !important;
  height: 45px !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  font-size: 1.2rem;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

.btn-carousel-new:hover:not(:disabled) {
  background: rgba(201, 207, 222, 0.95) !important;
  transform: translateY(-50%) scale(1.05) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.btn-carousel-new:disabled {
  opacity: 0.3 !important;
  cursor: not-allowed !important;
  background: rgba(239, 241, 247, 0.5) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.btn-carousel-new.prev {
  left: -20px;
}

.btn-carousel-new.next {
  right: -20px;
}


.product-image-frame {
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 10px;
}

.product-details-new {
  max-width: 400px;
  text-align: left;
}

.category-text {
  color: #7d8bb2;
  font-weight: 500;
  letter-spacing: 0.5px;
  font-size: 14px;
  margin-bottom: 4px;
  display: block;
}

.product-name-new {
  color: #2b3d8f;
  font-weight: 700;
  font-size: 24px;
  margin-bottom: 8px;
}

.product-ref-new {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 8px;
}

.product-desc-new {
  color: #333;
  font-size: 14px;
  line-height: 1.5;
}

/* Pricing Section */
.pricing-container-new {
  border: 1.5px solid #bdc4d9;
  border-radius: 15px;
  padding: 30px 40px;
  position: relative;
  margin-top: 30px;
}

.section-divider {
  position: absolute;
  top: -14px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.divider-text {
  background: white;
  padding: 0 20px;
  color: #2b3d8f;
  font-weight: 700;
  font-size: 14px;
}

.pricing-container-new::before {
  content: "";
  position: absolute;
  top: -1px;
  left: 10%;
  right: 10%;
  height: 2px;
  background: white;
  z-index: 0;
}

.label-control {
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
  display: block;
  font-weight: 500;
}

/* Pricing Inputs */
.form-control {
  border: 1.5px solid #bdc4d9;
  border-radius: 50px !important;
  padding: 10px 20px;
  background-color: #f8f9fa;
  font-weight: 500;
  color: #333;
  height: 48px;
  box-shadow: none !important;
  outline: none !important;
}

.form-control:focus {
  border-color: #2b3d8f;
}

.form-control:disabled {
  background-color: #f0f2f5;
  color: #555;
  border-color: #d1d8e6;
}

.input-group-pill {
  display: flex;
  align-items: stretch;
  width: 100%;
}

.pill-left {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
  flex: 1;
}

.input-group-addon-pill {
  display: flex;
  align-items: center;
  padding: 0 15px;
  background-color: #fff;
  border: 1.5px solid #909090;
  border-left: none;
  border-radius: 0 50px 50px 0;
  color: #333;
  font-weight: 500;
}

.input-group-pill:focus-within .pill-left,
.input-group-pill:focus-within .input-group-addon-pill {
  border-color: #2b3d8f;
}

/* Footer Buttons */
/* Custom Checkbox within button */
.custom-checkbox-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 20px;
  height: 20px;
}

.custom-checkbox-wrapper input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.custom-checkbox {
  position: absolute;
  top: 0;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: transparent;
  border: 2px solid white;
  border-radius: 4px;
}

.custom-checkbox-wrapper input:checked ~ .custom-checkbox {
  background-color: transparent;
}

.custom-checkbox:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.custom-checkbox-wrapper input:checked ~ .custom-checkbox:after {
  display: block;
}

.btn-apply-recommended {
  background-color: #1a275a;
  color: white;
  border: none;
  border-radius: 22px;
  padding: 12px 25px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.btn-apply-recommended:hover {
  background-color: #0d163d;
  transform: translateY(-2px);
}

.btn-validate-new {
  background-color: #f37021;
  color: white;
  border: none;
  border-radius: 22px;
  padding: 12px 60px;
  font-weight: 700;
  font-size: 16px;
  transition: all 0.3s ease;
}

.btn-validate-new:hover {
  background-color: #d95e16;
  transform: translateY(-2px);
}

@media (max-width: 991px) {
  .max-width-65 {
    max-width: 95% !important;
  }
  .product-info-card {
    flex-direction: column;
    text-align: center;
  }
  .product-details-new {
    text-align: center;
  }
}
</style>
